import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../context/AuthContext';
import { useProjects } from '../../../context/ProjectContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { generateChatTitle } from '../../../utils/titleGenerator';
import { SessionManager } from '../../../utils/sessionManager';
import { InfoModal } from '../../common/InfoModal';
import DragDropOverlay from '../../common/DragDropOverlay';
import SavedPromptsModal from '../../common/SavedPromptsModal';
import SavePromptDialog from '../../common/SavePromptDialog';
import AddToNotesModal from '../../notes/AddToNotesModal';
import { MessageSquare, MessageCircle, FileText, Video, Link, Mic, Volume2, Upload, Zap, Brain, Share2, Copy, FileDown } from 'lucide-react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

// Import our custom hooks
import { useAudioManager, useFileManager, useDragAndDrop } from '../../../hooks';
import { useProjectActionCable } from '../../../hooks/useProjectActionCable';
import TeamChatDrawer from '../../../components/team-chat/TeamChatDrawer';

// Team chat FAB pulse animation
if (!document.getElementById('team-chat-pulse-style')) {
    const style = document.createElement('style');
    style.id = 'team-chat-pulse-style';
    style.textContent = `
      @keyframes team-chat-pulse {
        0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.4); }
        50% { transform: scale(1.04); box-shadow: 0 0 0 8px rgba(37, 99, 235, 0); }
      }
      .team-chat-pulse { animation: team-chat-pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
      .team-chat-pulse:hover { animation: none; transform: scale(1); }
      @media (prefers-reduced-motion: reduce) { .team-chat-pulse { animation: none; } }
    `;
    document.head.appendChild(style);
}

// Import our components
import {
    ChatHeader,
    TTSSettingsPanel,
    MessageList,
    FilePreviewArea,
    ChatInputArea
} from './chat';

// Constants
import { getConfig } from '../../../config/production';
import { modelServicePost, modelServiceUpload } from '../../../utils/modelService';
import { sanitizeErrorMessage } from '../../../utils/errorMessages';
import { stripContextMarkers } from './chat/messageText';

const API_URL_BASE = getConfig('API_URL') || 'https://api.jpro.app/api/v1';
const cleanUrl = API_URL_BASE.endsWith('/') ? API_URL_BASE.slice(0, -1) : API_URL_BASE;
const API_URL = cleanUrl.endsWith('/api/v1') ? cleanUrl : `${cleanUrl}/api/v1`;

function UnifiedChat() {
    // Core state
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [chatSessionId, setChatSessionId] = useState(null);
    const [chatId, setChatId] = useState(null);
    const [showLengthWarning, setShowLengthWarning] = useState(false);
    const [currentTitle, setCurrentTitle] = useState('New Chat');
    const [currentChatPinned, setCurrentChatPinned] = useState(false);
    const [ragSessionId, setRagSessionId] = useState(null);
    const [copiedStates, setCopiedStates] = useState({});
    const [youtubeUrls, setYoutubeUrls] = useState([]); // Store multiple YouTube URLs for video understanding
    const [showGuide, setShowGuide] = useState(false);
    const [showTeamChat, setShowTeamChat] = useState(false);
    const [teamChatHidden, setTeamChatHidden] = useState(() => localStorage.getItem('team_chat_hidden') === 'true');
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [savingContent, setSavingContent] = useState('');
    const [showSaveToNotes, setShowSaveToNotes] = useState(false);
    const [noteContent, setNoteContent] = useState('');
    const [showPromptsModal, setShowPromptsModal] = useState(false);
    const [typingUsers, setTypingUsers] = useState([]); // Track who's typing
    const [processingUser, setProcessingUser] = useState(null); // Track whose request is being processed
    const typingTimeoutRef = useRef(null);
    const processedMessageIds = useRef(new Set()); // Track processed message IDs to prevent duplicates

    // TTS Settings
    const [showTTSSettings, setShowTTSSettings] = useState(false);
    const [ttsSettings, setTTSSettings] = useState({
        voice: 'Kore',
        style: 'normal',
        pace: 'normal',
        enabled: true
    });

    // Reasoning Mode — initial value: server pref → localStorage cache → 'fast'
    const [reasoningMode, setReasoningMode] = useState(() => {
        const cached = localStorage.getItem('jpro_default_reasoning_mode');
        return cached === 'deep' || cached === 'fast' ? cached : 'fast';
    });

    // Refs
    const messagesEndRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const pendingInterruptRef = useRef(null); // queued user message while AI is thinking
    const userMessagePersistedRef = useRef(false); // true if collaborative-chat early save succeeded for the current in-flight message

    // Custom hooks
    const { audioStates, handleAudio, getAudioIconType, initializeAudioState, cleanupAudio } = useAudioManager();
    const { uploadedFiles, handleFileUpload, removeFile, clearFiles } = useFileManager();
    const { isDragging, dragHandlers } = useDragAndDrop(handleFileUpload);

    // Auth and navigation
    const { token, handleApiResponse, currentUser, canAccessTeamChat, updateUiPreferences } = useAuth();
    const userDefaultReasoningMode = currentUser?.uiPreferences?.default_reasoning_mode || null;
    const { currentProject, getProjectContext, selectProject, projects } = useProjects();
    const navigate = useNavigate();
    const location = useLocation();

    // When the server-side default loads (or changes elsewhere), adopt it as the active mode.
    // localStorage already seeded the initial value to avoid flicker; this just keeps them in sync.
    useEffect(() => {
        if (userDefaultReasoningMode && userDefaultReasoningMode !== reasoningMode) {
            setReasoningMode(userDefaultReasoningMode);
            localStorage.setItem('jpro_default_reasoning_mode', userDefaultReasoningMode);
        }
        // Intentionally only re-run when the server pref changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userDefaultReasoningMode]);

    const handlePinReasoningMode = async () => {
        const next = userDefaultReasoningMode === reasoningMode ? null : reasoningMode;
        // Optimistic localStorage update so reload reflects it instantly
        if (next) localStorage.setItem('jpro_default_reasoning_mode', next);
        else localStorage.removeItem('jpro_default_reasoning_mode');

        // PATCH server. Sending null clears the default.
        const result = await updateUiPreferences({ default_reasoning_mode: next });
        if (!result.success) {
            toast.error('Could not save default mode');
        } else {
            toast.success(next ? `Default set to ${next === 'fast' ? 'Fast' : 'Deep'}` : 'Default cleared');
        }
    };

    // Real-time event handler for Action Cable
    const handleRealtimeEvent = useCallback((data) => {
        console.log('📡 Real-time event in UnifiedChat:', data);

        // Filter by project_id - ignore events from other projects
        if (data.project_id && currentProject?.id && data.project_id !== currentProject.id) {
            return;
        }

        if (data.type === 'user_typing' && data.chat_id === chatId) {
            // Add user to typing list
            setTypingUsers(prev => {
                if (!prev.find(u => u.id === data.user.id)) {
                    return [...prev, data.user];
                }
                return prev;
            });
        } else if (data.type === 'user_stopped_typing' && data.chat_id === chatId) {
            // Remove user from typing list
            setTypingUsers(prev => prev.filter(u => u.id !== data.user.id));
        } else if (data.type === 'message_created') {
            // Only add message if it's for the current chat
            if (data.chat_id === chatId) {
                // Skip own messages - sender already has them locally
                // (user message added optimistically, AI response added from model service)
                if (data.sender && currentUser &&
                    String(data.sender.id) === String(currentUser.id)) {
                    console.log('⏭️  Skipping own message (already shown locally)');
                    return;
                }

                // Check if we've already processed this message (prevents React Strict Mode duplicates)
                if (processedMessageIds.current.has(data.message.id)) {
                    console.log(`⏭️  Already processed message ID: ${data.message.id}`);
                    return;
                }

                // Mark as processed
                processedMessageIds.current.add(data.message.id);
                // Parse metadata if it's a string
                let metadata = {};
                if (typeof data.message.metadata === 'string') {
                    try {
                        metadata = JSON.parse(data.message.metadata);
                    } catch (e) {
                        console.warn('Failed to parse metadata:', e);
                    }
                } else {
                    metadata = data.message.metadata || {};
                }

                // Transform to match expected format
                const newMessage = {
                    id: data.message.id,
                    text: data.message.content,
                    isUser: data.message.is_user,
                    model: data.message.is_user ? 'user' : (metadata.model || 'ai'),
                    timestamp: data.message.created_at,
                    modelName: metadata.modelName || 'AI Assistant',
                    provider: metadata.provider || 'System',
                    search_suggestions: metadata.search_suggestions || null,
                    grounding_info: metadata.grounding_info || null,
                    is_housing_related: metadata.is_housing_related || false,
                    files: [],
                    image_data: null,
                    sender: data.sender || null // Include sender info for collaborative chats
                };

                // Track processing state
                if (data.message.is_user) {
                    // User message received - show processing indicator
                    setProcessingUser(data.sender);
                } else {
                    // AI response received - clear processing indicator
                    setProcessingUser(null);
                }

                // Add message (no duplicates possible since we track processed IDs)
                setMessages(prevMessages => {
                    console.log(`📨 Adding message ID: ${newMessage.id}`);
                    return [...prevMessages, newMessage];
                });
            }
        } else if (data.type === 'chat_created') {
            // Show notification when a new chat is created
            toast(`${data.creator.username} started a new chat: ${data.chat.title}`);
        } else if (data.type === 'chat_updated') {
            // If the current chat was updated, update the title
            if (data.chat.id === chatId) {
                setCurrentTitle(data.chat.title);
                toast('Chat updated');
            }
        } else if (data.type === 'chat_deleted' || data.type === 'chat_removed') {
            // If the current chat was deleted, show warning
            if (data.chat_id === chatId) {
                toast('This chat has been deleted', { icon: '⚠️' });
                // Optionally redirect after a delay
                setTimeout(() => {
                    if (currentProject) {
                        navigate(`/dashboard/projects/${currentProject.id}`);
                    } else {
                        navigate('/dashboard/unified-chat');
                    }
                }, 2000);
            }
        }
    }, [chatId, currentProject, currentUser, navigate]);

    // Connect to Action Cable for real-time updates (only for collaborative projects)
    const { isConnected, sendTyping, sendStoppedTyping } = useProjectActionCable(
        currentProject?.is_collaborative ? currentProject.id : null,
        handleRealtimeEvent
    );

    // Clear processed message IDs and typing timeout when chat changes
    useEffect(() => {
        processedMessageIds.current.clear();
        return () => {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
        };
    }, [chatId]);

    // Listen for team chat visibility changes from settings
    useEffect(() => {
        const handler = () => setTeamChatHidden(localStorage.getItem('team_chat_hidden') === 'true');
        window.addEventListener('team-chat-visibility-changed', handler);
        return () => window.removeEventListener('team-chat-visibility-changed', handler);
    }, []);

    // Handle input change with typing indicator
    const handleInputChange = (value) => {
        setInputMessage(value);

        // Send typing notification
        if (currentProject?.is_collaborative && chatId) {
            sendTyping(chatId);

            // Clear existing timeout
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }

            // Set timeout to send stopped typing after 2 seconds of inactivity
            typingTimeoutRef.current = setTimeout(() => {
                sendStoppedTyping(chatId);
            }, 2000);
        }
    };

    // Auto scroll to bottom when messages change and check chat length
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        const MESSAGE_THRESHOLD = 60;
        if (messages.length > MESSAGE_THRESHOLD && !showLengthWarning) {
            setShowLengthWarning(true);
        }
    }, [messages, showLengthWarning]);

    // Generate and store a consistent session ID at the beginning
    useEffect(() => {
        if (!chatSessionId) {
            const newSessionId = `unified_chat_${Date.now()}`;
            setChatSessionId(newSessionId);
            
            localStorage.removeItem('unified_current_session');
            localStorage.removeItem('jpro_current_session');
        }
    }, [chatSessionId]);
    
    // Store the RAG session ID only for continuous use
    useEffect(() => {
        if (ragSessionId) {
            localStorage.setItem('unified_rag_session_id', ragSessionId);
        }
        
        return () => {
            if (ragSessionId) {
                localStorage.setItem('unified_rag_session_id', ragSessionId);
            }
        };
    }, [ragSessionId]);

    // Load existing chat or start new one (keeping the original complex logic)
    useEffect(() => {
        const loadExistingChat = async () => {
            try {
                setIsLoading(true);
                
                const params = new URLSearchParams(location.search);
                let continueChatId = params.get('chat_id');
                
                if (!continueChatId) {
                    continueChatId = localStorage.getItem('unified_current_chat_id') || localStorage.getItem('jpro_current_chat_id');
                    
                    if (!continueChatId) {
                        const sessionManagerData = SessionManager.getSessionRedundantly();
                        if (sessionManagerData.chatId) {
                            continueChatId = sessionManagerData.chatId;
                        }
                    }
                }
                
                // Clear all storage methods to prevent stale data in future navigation
                localStorage.removeItem('unified_current_chat_id');
                localStorage.removeItem('unified_current_session');
                localStorage.removeItem('unified_rag_session_id');
                localStorage.removeItem('jpro_current_chat_id');
                localStorage.removeItem('jpro_current_session');
                SessionManager.clearSessionRedundantly();
                
                let continueSessionId = localStorage.getItem('unified_current_session') || localStorage.getItem('jpro_current_session');
                const continueRagSessionId = localStorage.getItem('unified_rag_session_id');
                
                if (continueRagSessionId) {
                    setRagSessionId(continueRagSessionId);
                }
                

                let chatData = null;

                // First try to load by chat ID if available
                if (continueChatId) {
                    try {
                        const chatIdInt = parseInt(continueChatId, 10);
                        if (!isNaN(chatIdInt)) {
                            setChatId(chatIdInt);
                            // Using apiGet with automatic token refresh
                            const { apiGet } = await import('../../../utils/api');
                            const response = await apiGet(`/chats/${chatIdInt}`);
                            if (response.ok) {
                                chatData = await response.json();
                            } else {
                                console.error('Failed to load chat by ID, status:', response.status);
                            }
                        } else {
                            console.error('Invalid chat ID format:', continueChatId);
                        }
                    } catch (error) { 
                        console.error('Error loading chat by ID:', error); 
                    }
                }

                // If chat ID load failed, try by session ID
                if (!chatData && continueSessionId) {
                    try {
                        setChatSessionId(continueSessionId);
                        // Using apiGet with automatic token refresh
                        const { apiGet } = await import('../../../utils/api');
                        const response = await apiGet(`/chats/by_session/${continueSessionId}`);

                        if (response.ok) {
                            chatData = await response.json();
                        } else {
                            console.error('Failed to load chat by session ID, status:', response.status);
                            if (response.status !== 404) {
                                console.error('Unexpected error response:', await response.text().catch(() => 'Unable to read response text'));
                            }
                        }
                    } catch (error) { 
                        console.error('Error loading chat by session ID:', error);
                    }
                }
                
                if (chatData?.chat?.messages?.length > 0) {
                    // Save chat info to component state
                    setChatId(chatData.chat.id);
                    if(chatData.chat.title) setCurrentTitle(chatData.chat.title);
                    setCurrentChatPinned(chatData.chat.pinned || false);
                    setChatSessionId(chatData.chat.session_id);

                    // Set or clear project based on chat's project_id
                    if (chatData.chat.project_id) {
                        // Chat belongs to a project - check if it's different from current
                        if (currentProject?.id !== chatData.chat.project_id) {
                            // First try to find in existing projects array (avoid API call)
                            const project = projects.find(p => p.id === chatData.chat.project_id);
                            if (project) {
                                selectProject(project);
                            } else {
                                // Not in cache, fetch it
                                const { apiGet } = await import('../../../utils/api');
                                try {
                                    const projectResponse = await apiGet(`/projects/${chatData.chat.project_id}`);
                                    if (projectResponse.ok) {
                                        const projectData = await projectResponse.json();
                                        selectProject(projectData.project);
                                    }
                                } catch (err) {
                                    console.warn('Failed to load project for chat:', err);
                                }
                            }
                        }
                    } else {
                        // Chat has no project - clear current project
                        if (currentProject) {
                            selectProject(null);
                        }
                    }

                    // Process and transform the messages (keeping original logic)
                    const loadedMessages = chatData.chat.messages.map(msg => {
                        let metadata = {};
                        if (msg.metadata) { 
                            try { 
                                metadata = JSON.parse(msg.metadata); 
                            } catch (e) { 
                                console.warn('Failed to parse message metadata:', e); 
                            } 
                        }
                        
                        
                        const processedAttachments = Array.isArray(msg.attachments) ? 
                            msg.attachments.map(attachment => {
                                return {
                                    id: attachment.id || `attachment-${Math.random()}`,
                                    name: attachment.name || 'File',
                                    type: attachment.content_type || '',
                                    url: attachment.url || null,
                                    size: attachment.size || null
                                };
                            }) : [];
                            
                        return {
                            id: msg.id,
                            text: msg.content,
                            isUser: msg.is_user,
                            model: msg.is_user ? 'user' : (metadata.model || 'ai'),
                            timestamp: msg.created_at,
                            modelName: metadata.modelName || 'AI Assistant',
                            provider: metadata.provider || 'System',
                            search_suggestions: metadata.search_suggestions || null,
                            grounding_info: metadata.grounding_info || null,
                            is_housing_related: metadata.is_housing_related || false,
                            files: processedAttachments,
                            image_data: metadata.image_generation ? {
                                prompt_used: metadata.image_prompt,
                                mime_type: metadata.image_mime_type,
                                needs_fetch: true
                            } : null,
                            sender: msg.sender || null // Include sender info for collaborative chats
                        };
                    });
                    
                    
                    // Fetch all documents and restore attachments/images for messages (keeping original logic)
                    let allDocuments = [];
                    try {
                        // Using apiGet with automatic token refresh
                        const { apiGet } = await import('../../../utils/api');
                        const docsResponse = await apiGet('/documents');
                        if (docsResponse.ok) {
                            const docsData = await docsResponse.json();
                            allDocuments = docsData.documents || [];
                        }
                    } catch (error) {
                        console.error('Failed to fetch documents:', error);
                    }

                    const messagesWithAttachments = await Promise.all(loadedMessages.map(async (msg) => {
                        let updatedMsg = { ...msg };

                        // Restore generated images
                        if (msg.image_data?.needs_fetch && allDocuments.length > 0) {
                            try {
                                const matchingImage = allDocuments.find(doc => 
                                    doc.is_generated && 
                                    doc.is_image &&
                                    doc.title?.includes(msg.image_data.prompt_used?.substring(0, 20)) &&
                                    Math.abs(new Date(doc.created_at) - new Date(msg.timestamp)) < 60000
                                );
                                
                                if (matchingImage?.id) {
                                    // Using apiGet with automatic token refresh
                                    const { apiGet } = await import('../../../utils/api');
                                    const imageResponse = await apiGet(`/documents/${matchingImage.id}/download`);

                                    if (imageResponse.ok) {
                                        const imageBlob = await imageResponse.blob();
                                        const imageBase64 = await new Promise((resolve, reject) => {
                                            const reader = new FileReader();
                                            reader.onloadend = () => {
                                                const result = reader.result;
                                                if (result && typeof result === 'string') {
                                                    resolve(result.split(',')[1]);
                                                } else {
                                                    reject(new Error('Failed to convert blob to base64'));
                                                }
                                            };
                                            reader.onerror = reject;
                                            reader.readAsDataURL(imageBlob);
                                        });
                                        
                                        updatedMsg.image_data = {
                                            prompt_used: msg.image_data.prompt_used,
                                            mime_type: msg.image_data.mime_type,
                                            image_base64: imageBase64
                                        };
                                    }
                                }
                            } catch (error) {
                                console.error('Failed to fetch generated image for message:', error);
                            }
                        }

                        // Restore uploaded file attachments
                        if (msg.files && msg.files.length > 0) {
                            try {
                                const restoredFiles = msg.files.map(file => {
                                    const matchingDoc = allDocuments.find(doc => 
                                        !doc.is_generated &&
                                        (doc.title === file.name || doc.title?.includes(file.name)) &&
                                        Math.abs(new Date(doc.created_at) - new Date(msg.timestamp)) < 300000
                                    );

                                    if (matchingDoc) {
                                        return {
                                            ...file,
                                            url: matchingDoc.download_url,
                                            id: matchingDoc.id
                                        };
                                    }
                                    return file;
                                });
                                
                                updatedMsg.files = restoredFiles;
                            } catch (error) {
                                console.error('Failed to restore file attachments for message:', error);
                            }
                        }

                        return updatedMsg;
                    }));
                    
                    setMessages([{
                        id: crypto.randomUUID(),
                        text: "Welcome back! Continue your conversation with me. I can help with housing questions or any other topics.",
                        isUser: false, 
                        model: "system"
                    }, ...messagesWithAttachments]);
                    
                    localStorage.setItem('unified_current_chat_id', chatData.chat.id.toString());
                    localStorage.removeItem('unified_current_session');
                    
                    const lastAiMessage = loadedMessages.filter(m => !m.isUser).pop();
                    if (lastAiMessage?.metadata?.rag_session_id) {
                        setRagSessionId(lastAiMessage.metadata.rag_session_id);
                    } else {
                        localStorage.removeItem('unified_rag_session_id');
                    }
                } else {
                    startNewChat();
                    
                    localStorage.removeItem('unified_current_chat_id');
                    localStorage.removeItem('unified_current_session');
                    localStorage.removeItem('unified_rag_session_id');
                }

            } catch (err) {
                console.error('Error loading chat history:', err);
                startNewChat();
                setError(null);
            } finally {
                setIsLoading(false);
            }
        };

        const startNewChat = () => {
            setMessages([{
                id: 1,
                text: "Hello! I'm your unified assistant. I can help with affordable housing questions and also answer general questions or work with your files and images. What can I help you with today?",
                isUser: false, 
                model: "system"
            }]);
            
            setChatId(null);
            setRagSessionId(null);
            setCurrentTitle('New Chat');
            setError(null);
            
            const newSessionId = `unified_chat_${Date.now()}`;
            setChatSessionId(newSessionId);
            
            localStorage.removeItem('unified_current_chat_id');
            localStorage.removeItem('unified_current_session');
            localStorage.removeItem('unified_rag_session_id');
            localStorage.removeItem('jpro_current_chat_id');
            localStorage.removeItem('jpro_current_session');
            SessionManager.clearSessionRedundantly();
            
            navigate('/dashboard/unified-chat', { replace: true });
        };

        loadExistingChat();
    }, [token, location.search, navigate]);

    // Helper function to extract YouTube URL from message
    const extractYoutubeUrl = (text) => {
        const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
        const match = text.match(youtubeRegex);
        return match ? match[0] : null;
    };

    // Main message sending function (keeping original logic)
    const handleSendMessage = async () => {
        if (!inputMessage.trim() && uploadedFiles.length === 0) return;

        // If AI is currently thinking, queue as interrupt context
        if (isLoading) {
            const interruptText = inputMessage.trim();
            if (!interruptText) return;
            pendingInterruptRef.current = interruptText;
            // Show the interrupt as a user message in chat
            setMessages(prev => [...prev, {
                id: crypto.randomUUID(),
                text: interruptText,
                isUser: true,
                isInterrupt: true,
                sender: currentProject?.is_collaborative && currentUser ? { id: currentUser.id, username: currentUser.username } : null
            }]);
            setInputMessage('');
            return;
        }

        const currentInput = inputMessage;
        const currentFiles = [...uploadedFiles];

        // Extract YouTube URL from message and add to collection if new
        const extractedUrl = extractYoutubeUrl(currentInput);
        const updatedYoutubeUrls = [...youtubeUrls];
        if (extractedUrl && !updatedYoutubeUrls.includes(extractedUrl)) {
            updatedYoutubeUrls.push(extractedUrl);
            setYoutubeUrls(updatedYoutubeUrls);
            console.log('Added YouTube URL:', extractedUrl);
        }

        // Create user message object with sender info for collaborative chats
        const userMessage = {
            id: crypto.randomUUID(),
            text: currentInput,
            isUser: true,
            files: currentFiles.map(file => ({
                id: file.id, preview: file.preview, type: file.type, name: file.name, data: file.data
            })),
            // Add sender info for collaborative chats
            sender: currentProject?.is_collaborative && currentUser ? {
                id: currentUser.id,
                username: currentUser.username
            } : null
        };

        // Always add user message immediately for instant feedback
        setMessages(prev => [...prev, userMessage]);

        setInputMessage('');
        clearFiles();
        setIsLoading(true);
        setError(null);

        const loadingMessageId = Date.now() + 0.5;
        setMessages(prev => [...prev, {
            id: loadingMessageId, text: "Thinking...", isUser: false, isLoading: true, model: "system"
        }]);

        // For collaborative projects, persist the user message before calling the AI so
        // the message_created broadcast fires immediately — this lets other members see
        // "AI is processing <user>'s request..." while the AI is thinking, rather than
        // only after the AI finishes (when both broadcasts would fire back-to-back and
        // the processing state would flash for a split second).
        let userMessagePersistedEarly = false;
        if (currentProject?.is_collaborative && chatId) {
            try {
                const filesToUpload = userMessage.files?.filter(f => f.data) || [];
                let response;
                if (filesToUpload.length > 0) {
                    const formData = new FormData();
                    formData.append('message[content]', userMessage.text);
                    formData.append('message[is_user]', true);
                    filesToUpload.forEach(file => {
                        formData.append('message[attachments][]', file.data, file.name);
                    });
                    const { apiUpload } = await import('../../../utils/api');
                    response = await apiUpload(`/chats/${chatId}/messages`, formData);
                } else {
                    const { apiPost } = await import('../../../utils/api');
                    response = await apiPost(`/chats/${chatId}/messages`, {
                        message: { content: userMessage.text, is_user: true }
                    });
                }
                userMessagePersistedEarly = response?.ok;
            } catch (err) {
                console.error('Failed to persist user message for collaborators:', err);
                // Non-fatal — saveConversation() will persist it when the AI response arrives.
            }
        }
        userMessagePersistedRef.current = userMessagePersistedEarly;

        try {
            let processedFiles = [];
            if (currentFiles.length > 0) {
                const uploadFormData = new FormData();
                currentFiles.forEach(file => uploadFormData.append('files', file.data, file.name));
                const uploadResponse = await modelServiceUpload('/upload', uploadFormData);
                
                if (!uploadResponse.ok) {
                    throw new Error((await uploadResponse.json().catch(() => ({}))).detail || 'File upload failed');
                }
                processedFiles = (await uploadResponse.json()).files || [];
                
                // Store processed files in chat memory for persistence
                if (processedFiles.length > 0 && chatId) {
                    await storeFilesInChat(chatId, processedFiles);
                }
            }

            const chatHistory = messages
                .filter(msg => !msg.isLoading && msg.model !== 'system')
                .map(msg => ({
                    sender: msg.isUser ? 'user' : 'model',
                    content: msg.text,
                    provider: msg.provider,
                    is_housing_related: msg.is_housing_related
                }));

            // Fetch project context if in a project (pass current query for relevance)
            let projectContext = null;
            if (currentProject) {
                try {
                    projectContext = await getProjectContext(currentProject.id, currentInput);
                } catch (err) {
                    console.warn('Failed to fetch project context:', err);
                }
            }

            const messageRequest = {
                message: currentInput,
                chat_history: chatHistory,
                files: processedFiles,
                rag_session_id: ragSessionId,
                chat_id: chatId,  // Add chat_id for file memory retrieval
                youtube_urls: updatedYoutubeUrls,  // Include all YouTube URLs for multi-video understanding
                project_context: projectContext,  // Include project context if available
                reasoning_mode: reasoningMode  // Include reasoning mode (fast/deep)
            };

            const response = await modelServicePost('/generate', messageRequest);

            if (!response.ok) {
                throw new Error((await response.json().catch(() => ({}))).detail || `Model service error: ${response.statusText}`);
            }

            const responseData = await response.json();
            if (!Array.isArray(responseData) || responseData.length === 0) {
                throw new Error('Invalid response format');
            }

            const modelResponse = responseData[0];

            if (modelResponse.rag_session_id) {
                setRagSessionId(modelResponse.rag_session_id);
            }

            const aiMessage = {
                id: crypto.randomUUID(),
                text: modelResponse.content,
                isUser: false,
                model: modelResponse.model || 'ai',
                modelName: modelResponse.modelName || "AI Assistant",
                provider: modelResponse.provider || "System",
                timestamp: modelResponse.timestamp,
                search_suggestions: modelResponse.search_suggestions || null,
                grounding_info: modelResponse.grounding_info || null,
                is_housing_related: modelResponse.is_housing_related || false,
                image_data: modelResponse.image_data || null
            };

            if (modelResponse.image_data) {
                try {
                    await storeImageAsDocument(
                        modelResponse.image_data.image_base64,
                        modelResponse.image_data.mime_type,
                        modelResponse.image_data.prompt_used
                    );
                } catch (storeError) {
                    console.error('Failed to store image as document:', storeError);
                }
            }

            setMessages(prev => prev.filter(msg => !msg.isLoading).concat(aiMessage));

            // Only skip if the early-save actually succeeded; a failed early save must
            // fall through to saveConversation's normal user-message persist path.
            saveConversation(userMessage, aiMessage, { skipUserMessage: userMessagePersistedRef.current });
            userMessagePersistedRef.current = false;
            initializeAudioState(aiMessage.id);

            // If user added context while AI was thinking, auto-send follow-up
            if (pendingInterruptRef.current) {
                const interruptText = pendingInterruptRef.current;
                pendingInterruptRef.current = null;
                // Brief delay so user sees the AI response before follow-up fires
                setTimeout(() => {
                    setInputMessage(`[Additional context for my previous question]: ${interruptText}`);
                    // Trigger send on next tick after state updates
                    setTimeout(() => {
                        const sendBtn = document.querySelector('[title="Send message"]');
                        if (sendBtn) sendBtn.click();
                    }, 100);
                }, 500);
            }

        } catch (err) {
            console.error('Error in handleSendMessage:', err);
            const safeMessage = sanitizeErrorMessage(err);
            setError(safeMessage);
            setMessages(prev => prev.filter(msg => !msg.isLoading).concat({
                id: crypto.randomUUID(),
                text: `Error: ${safeMessage}`,
                isUser: false,
                model: "system",
                isError: true
            }));
            // Clear any pending interrupt on error
            pendingInterruptRef.current = null;
        } finally {
            setIsLoading(false);
        }
    };

    // Store files in chat for persistent memory
    const storeFilesInChat = async (chatId, processedFiles) => {
        if (!chatId || !processedFiles.length) return;
        
        try {
            for (const file of processedFiles) {
                // Determine file type for storage
                let fileType = 'text';
                if (file.content_type?.includes('excel') || file.content_type?.includes('spreadsheet')) {
                    fileType = 'excel';
                } else if (file.content_type?.includes('word') || file.content_type?.includes('document')) {
                    fileType = 'word';
                } else if (file.content_type?.includes('powerpoint') || file.content_type?.includes('presentation')) {
                    fileType = 'powerpoint';
                } else if (file.content_type?.includes('pdf')) {
                    fileType = 'pdf';
                } else if (file.content_type?.startsWith('image/')) {
                    fileType = 'image';
                }

                // Using apiPost with automatic token refresh
                const { apiPost } = await import('../../../utils/api');
                const response = await apiPost(`/chats/${chatId}/file_memories`, {
                    file_memory: {
                        filename: file.filename,
                        file_type: fileType,
                        processed_content: file.content || `File uploaded: ${file.filename}` // Use processed text content from Python
                    }
                });
                
                if (response.ok) {
                    console.log(`Stored file ${file.filename} in chat memory`);
                } else {
                    console.warn(`Failed to store file ${file.filename}:`, response.status);
                }
            }
        } catch (error) {
            console.error('Error storing files in chat:', error);
        }
    };

    // Store image as document function (keeping original)
    const storeImageAsDocument = async (imageBase64, mimeType, prompt) => {
        try {
            // Using apiPost with automatic token refresh
            const { apiPost } = await import('../../../utils/api');
            const response = await apiPost('/documents/store_generated_image', {
                image_data: imageBase64,
                mime_type: mimeType,
                prompt: prompt
            });

            if (!response.ok) {
                throw new Error(`Failed to store image: ${response.statusText}`);
            }

            const result = await response.json();
            return result;

        } catch (error) {
            console.error('Error storing image as document:', error);
            throw error;
        }
    };

    // Save conversation function (keeping original)
    // skipUserMessage: set to true when the user message has already been persisted
    // ahead of the AI call (collaborative chats, to broadcast the "AI is processing"
    // state to other members immediately).
    const saveConversation = async (userMessage, aiMessage, { skipUserMessage = false } = {}) => {
        if (!token) return;
        let currentChatId = chatId;

        try {
            if (!currentChatId) {
                const chatPayload = {
                    chat: {
                        title: "New Chat",
                        session_id: chatSessionId,
                        project_id: currentProject?.id || null
                    }
                };

                // Using apiPost with automatic token refresh
                const { apiPost } = await import('../../../utils/api');
                const chatResponse = await apiPost('/chats', chatPayload);

                if (!chatResponse.ok) {
                    throw new Error(`Failed to create chat: ${(await chatResponse.json().catch(()=>({}))).error || chatResponse.statusText}`);
                }

                const chatData = await chatResponse.json();
                currentChatId = chatData.chat.id;
                setChatId(currentChatId);

                try {
                    const title = await generateChatTitle({
                        userMessage: userMessage.text,
                        aiResponse: aiMessage.text,
                        chatType: 'unified'
                    });

                    setCurrentTitle(title);
                    // Using apiPatch with automatic token refresh
                    const { apiPatch } = await import('../../../utils/api');
                    await apiPatch(`/chats/${currentChatId}`, { chat: { title: title } });
                } catch (titleError) { 
                    console.error('Failed to generate/update title:', titleError); 
                }
            }

            // Save User Message with attachments if present (keeping original logic).
            // Skipped for collaborative chats that already persisted it before the AI call.
            if (!skipUserMessage) {
                if (userMessage.files && userMessage.files.length > 0) {

                    const formData = new FormData();
                    formData.append('message[content]', userMessage.text);
                    formData.append('message[is_user]', true);

                    const filesToUpload = userMessage.files.filter(f => f.data);

                    filesToUpload.forEach(file => {
                        formData.append('message[attachments][]', file.data, file.name);
                    });

                    // Using apiUpload with automatic token refresh
                    const { apiUpload } = await import('../../../utils/api');
                    await apiUpload(`/chats/${currentChatId}/messages`, formData);
                } else {
                    const userMessagePayload = {
                        message: {
                            content: userMessage.text,
                            is_user: true
                        }
                    };

                    // Using apiPost with automatic token refresh
                    const { apiPost } = await import('../../../utils/api');
                    await apiPost(`/chats/${currentChatId}/messages`, userMessagePayload);
                }
            }

            // Save AI Message with all metadata
            const aiMessagePayload = {
                message: {
                    content: aiMessage.text,
                    is_user: false,
                    metadata: JSON.stringify({
                        modelName: aiMessage.modelName,
                        provider: aiMessage.provider,
                        model: aiMessage.model,
                        search_suggestions: aiMessage.search_suggestions,
                        grounding_info: aiMessage.grounding_info,
                        is_housing_related: aiMessage.is_housing_related,
                        rag_session_id: ragSessionId,
                        source_system: aiMessage.is_housing_related ? 'rag' : 'gemini',
                        image_generation: aiMessage.image_data ? true : false,
                        image_prompt: aiMessage.image_data?.prompt_used || null,
                        image_mime_type: aiMessage.image_data?.mime_type || null,
                        image_data: aiMessage.image_data || null,
                    })
                }
            };

            // Using apiPost with automatic token refresh
            const { apiPost } = await import('../../../utils/api');
            await apiPost(`/chats/${currentChatId}/messages`, aiMessagePayload);

        } catch (err) {
            console.error('Error during saveConversation:', err);
            setError(`Failed to save conversation: ${err.message}`);
        }
    };

    // Speech-to-Text functionality (keeping original logic)
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const options = { mimeType: 'audio/webm;codecs=opus' };
            
            if (!MediaRecorder.isTypeSupported(options.mimeType)) options.mimeType = 'audio/webm';
            if (!MediaRecorder.isTypeSupported(options.mimeType)) throw new Error("Audio recording format not supported.");

            mediaRecorderRef.current = new MediaRecorder(stream, options);
            audioChunksRef.current = [];
            
            mediaRecorderRef.current.ondataavailable = e => e.data.size > 0 && audioChunksRef.current.push(e.data);
            mediaRecorderRef.current.onstop = () => {
                if (audioChunksRef.current.length === 0) return;
                
                const audioBlob = new Blob(audioChunksRef.current, { type: options.mimeType });
                const audioFile = new File([audioBlob], `recording_${Date.now()}.webm`, { type: options.mimeType });
                
                handleFileUpload([audioFile]);
                transcribeAudio(audioBlob);
                
                stream.getTracks().forEach(track => track.stop());
            };
            
            mediaRecorderRef.current.start();
            setIsRecording(true); 
            setError(null);
        } catch (error) { 
            console.error('Error starting recording:', error); 
            setError(`Mic error: ${error.message}`); 
            setIsRecording(false); 
        }
    };
    
    const stopRecording = () => {
        if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
        setIsRecording(false);
    };

    // Speech transcription using backend
    const transcribeAudio = async (audioBlob) => {
        try {
            setIsLoading(true);
            
            const formData = new FormData();
            formData.append('audio', audioBlob, 'recording.webm');
            
            const response = await modelServiceUpload('/transcribe', formData);
            
            if (!response.ok) {
                throw new Error(`Transcription failed: ${response.statusText}`);
            }
            
            const data = await response.json();
            
            if (data.text) {
                setInputMessage(prev => prev + (prev ? ' ' : '') + data.text);
            } else {
                console.error('Transcription failed:', data);
            }
        } catch (error) {
            console.error('Error transcribing audio:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Copy message text to clipboard with formatting
    const handleCopy = async (messageId, rawText) => {
        try {
            // The chat strips the RAG source markers before rendering, so copying the
            // raw text pasted labels the reader never saw into their document.
            const text = stripContextMarkers(rawText);
            // Import marked for markdown to HTML conversion
            const { marked } = await import('marked');

            // Configure marked
            marked.setOptions({
                breaks: true,
                gfm: true
            });

            // Convert markdown to HTML
            const messageHtml = marked.parse(text);

            // Wrap with styling
            const styledHtml = `
                <div style="font-family: system-ui, -apple-system, sans-serif; line-height: 1.6; color: #374151;">
                    <style>
                        table { border-collapse: collapse; width: 100%; margin: 12px 0; }
                        th, td { border: 1px solid #D1D5DB; padding: 8px 12px; text-align: left; }
                        th { background-color: #F3F4F6; font-weight: 600; }
                        ul, ol { margin: 8px 0; padding-left: 24px; }
                        li { margin: 4px 0; }
                        code { background-color: #F3F4F6; padding: 2px 6px; border-radius: 3px; font-family: monospace; }
                        pre { background-color: #F3F4F6; padding: 12px; border-radius: 6px; overflow-x: auto; }
                        strong { font-weight: 600; }
                        em { font-style: italic; }
                    </style>
                    ${messageHtml}
                </div>
            `;

            // Copy both HTML and plain text
            const clipboardItem = new ClipboardItem({
                'text/html': new Blob([styledHtml], { type: 'text/html' }),
                'text/plain': new Blob([text], { type: 'text/plain' })
            });

            await navigator.clipboard.write([clipboardItem]);
            setCopiedStates(prev => ({ ...prev, [messageId]: true }));

            setTimeout(() => {
                setCopiedStates(prev => ({ ...prev, [messageId]: false }));
            }, 2000);
        } catch (error) {
            console.error('Failed to copy text:', error);
        }
    };

    // Audio toggle handler
    const handleAudioToggle = (messageId, text) => {
        // Without this, TTS reads the source labels out: "bracket DOCUMENT colon
        // Iowa underscore Lihtc underscore Compliance..." in the middle of a sentence.
        handleAudio(messageId, stripContextMarkers(text), ttsSettings);
    };

    // Chat update handler - refresh chat data
    const handleChatUpdated = async () => {
        if (!chatId) return;
        try {
            const { apiGet } = await import('../../../utils/api');
            const response = await apiGet(`/chats/${chatId}`);
            if (response.ok) {
                const chatData = await response.json();
                if (chatData.chat) {
                    setCurrentTitle(chatData.chat.title || 'New Chat');
                    setCurrentChatPinned(chatData.chat.pinned || false);
                    // Update project if changed
                    if (chatData.chat.project_id) {
                        const project = projects.find(p => p.id === chatData.chat.project_id);
                        if (project && currentProject?.id !== project.id) {
                            selectProject(project);
                        }
                    } else if (currentProject) {
                        // Chat removed from project
                        selectProject(null);
                    }
                }
            }
        } catch (error) {
            console.error('Failed to refresh chat:', error);
        }
    };

    // Chat delete handler - start new chat
    const handleChatDeleted = () => {
        handleNewChat();
    };

    // Export chat as PDF (captures actual rendered UI)
    const handleExportPDF = async () => {
        if (messages.length === 0) {
            alert('No messages to export');
            return;
        }

        try {
            // Create a temporary container for clean rendering
            const exportContainer = document.createElement('div');
            exportContainer.style.position = 'absolute';
            exportContainer.style.left = '-9999px';
            exportContainer.style.width = '800px';
            exportContainer.style.padding = '20px';
            exportContainer.style.backgroundColor = 'white';
            document.body.appendChild(exportContainer);

            // Add title and metadata
            const header = document.createElement('div');
            header.innerHTML = `
                <h1 style="color: #1976d2; font-size: 24px; margin-bottom: 10px; font-family: Arial, sans-serif;">${currentTitle}</h1>
                <p style="color: #666; font-size: 12px; margin-bottom: 5px;">Exported on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                <p style="color: #666; font-size: 12px; margin-bottom: 20px;">Total Messages: ${messages.length}</p>
                <hr style="border: 1px solid #1976d2; margin-bottom: 20px;" />
            `;
            exportContainer.appendChild(header);

            // Clone the actual message elements for accurate rendering
            const messageElements = document.querySelectorAll('[data-message-id]');
            messageElements.forEach(msgEl => {
                const clone = msgEl.cloneNode(true);
                exportContainer.appendChild(clone);
            });

            // Capture the container as canvas
            const canvas = await html2canvas(exportContainer, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff'
            });

            // Remove temporary container
            document.body.removeChild(exportContainer);

            // Convert to PDF with proper pagination
            const imgData = canvas.toDataURL('image/png');

            // Use standard Letter size (8.5 x 11 inches = 612 x 792 points)
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'pt',
                format: 'letter'
            });

            // PDF page dimensions in points
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // Calculate scaling to fit width
            const imgWidth = pageWidth;
            const imgHeight = (canvas.height * pageWidth) / canvas.width;

            // Calculate how many pages we need
            const totalPages = Math.ceil(imgHeight / pageHeight);

            // Add pages with content
            for (let page = 0; page < totalPages; page++) {
                if (page > 0) {
                    pdf.addPage();
                }

                // Calculate the portion of the image for this page
                const sourceY = (page * pageHeight * canvas.width) / pageWidth;
                const sourceHeight = (pageHeight * canvas.width) / pageWidth;

                // Add the image slice to the current page
                pdf.addImage(
                    imgData,
                    'PNG',
                    0,
                    -page * pageHeight,
                    imgWidth,
                    imgHeight
                );
            }

            // Save the PDF
            const filename = `${currentTitle.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.pdf`;
            pdf.save(filename);
        } catch (error) {
            console.error('Failed to export PDF:', error);
            alert('Failed to export PDF. Please try again.');
        }
    };

    // Copy chat to clipboard with formatting preserved
    const handleCopyToClipboard = async () => {
        if (messages.length === 0) return;

        try {
            // Import marked for markdown to HTML conversion
            const { marked } = await import('marked');

            // Configure marked to support tables and other features
            marked.setOptions({
                breaks: true,
                gfm: true
            });

            // Format messages as HTML with styling
            const htmlContent = messages.map(msg => {
                const sender = msg.isUser ? 'You' : 'Assistant';
                const senderStyle = msg.isUser
                    ? 'color: #3B82F6; font-weight: bold;'
                    : 'color: #8B5CF6; font-weight: bold;';

                // Convert markdown to HTML
                const messageHtml = marked.parse(stripContextMarkers(msg.text));

                return `
                    <div style="margin-bottom: 20px; font-family: system-ui, -apple-system, sans-serif;">
                        <div style="${senderStyle} margin-bottom: 8px;">${sender}:</div>
                        <div style="color: #374151; line-height: 1.6;">
                            ${messageHtml}
                        </div>
                    </div>
                `;
            }).join('');

            // Wrap in a container with basic table styling
            const styledHtml = `
                <div style="max-width: 800px;">
                    <style>
                        table { border-collapse: collapse; width: 100%; margin: 12px 0; }
                        th, td { border: 1px solid #D1D5DB; padding: 8px 12px; text-align: left; }
                        th { background-color: #F3F4F6; font-weight: 600; }
                        ul, ol { margin: 8px 0; padding-left: 24px; }
                        li { margin: 4px 0; }
                        code { background-color: #F3F4F6; padding: 2px 6px; border-radius: 3px; font-family: monospace; }
                        pre { background-color: #F3F4F6; padding: 12px; border-radius: 6px; overflow-x: auto; }
                        strong { font-weight: 600; }
                        em { font-style: italic; }
                    </style>
                    ${htmlContent}
                </div>
            `;

            // Create plain text fallback (for apps that don't support HTML)
            const plainText = messages.map(msg => {
                const sender = msg.isUser ? 'You' : 'Assistant';
                return `${sender}: ${stripContextMarkers(msg.text)}`;
            }).join('\n\n');

            // Copy both HTML and plain text to clipboard
            const clipboardItem = new ClipboardItem({
                'text/html': new Blob([styledHtml], { type: 'text/html' }),
                'text/plain': new Blob([plainText], { type: 'text/plain' })
            });

            await navigator.clipboard.write([clipboardItem]);

            // Show success notification
            alert('Chat copied to clipboard with formatting!');
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
            alert('Failed to copy to clipboard');
        }
    };

    // New chat handler
    const handleNewChat = async () => {
        localStorage.removeItem('unified_current_chat_id');
        localStorage.removeItem('unified_current_session');
        localStorage.removeItem('unified_rag_session_id');
        localStorage.removeItem('jpro_current_chat_id');
        localStorage.removeItem('jpro_current_session');

        SessionManager.clearSessionRedundantly();
        cleanupAudio();
        clearFiles();

        // Keep the current project selected - user stays in the same project
        const projectContextMessage = currentProject
            ? `Hello! Starting a new chat in project "${currentProject.name}". I have access to the context and memory from previous conversations in this project. How can I help you?`
            : "Hello! I'm your unified assistant. I can help with affordable housing questions and also answer general questions or work with your files and images. What can I help you with today?";

        // Clear current state but keep project
        setMessages([{
            id: 1,
            text: projectContextMessage,
            isUser: false,
            model: "system"
        }]);
        setChatId(null);
        setRagSessionId(null);
        setCurrentTitle('New Chat');
        setError(null);

        // Create new session ID
        const newSessionId = `unified_chat_${Date.now()}`;
        setChatSessionId(newSessionId);

        // Navigate with replace to ensure it works even if already on the route
        navigate('/dashboard/unified-chat', { replace: true });
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupAudio();
            clearFiles();
        };
    }, []);

    return (
        <div className="relative h-screen flex flex-col bg-white dark:bg-gray-900 overflow-hidden max-w-screen-2xl mx-auto" {...dragHandlers}>
            {/* Chat header */}
            <ChatHeader
                currentTitle={currentTitle}
                currentChatId={chatId}
                currentChatPinned={currentChatPinned}
                showTTSSettings={showTTSSettings}
                onToggleTTSSettings={() => setShowTTSSettings(!showTTSSettings)}
                isLive={currentProject?.is_collaborative && isConnected}
                onNewChat={handleNewChat}
                onShowGuide={() => setShowGuide(true)}
                onChatUpdated={handleChatUpdated}
                onChatDeleted={handleChatDeleted}
                onExportPDF={handleExportPDF}
                onCopyToClipboard={handleCopyToClipboard}
            />

            {/* TTS Settings Panel */}
            <TTSSettingsPanel
                showTTSSettings={showTTSSettings}
                ttsSettings={ttsSettings}
                onSettingsChange={setTTSSettings}
            />

            {/* Chat Messages */}
            <MessageList
                messages={messages}
                showLengthWarning={showLengthWarning}
                error={error}
                audioStates={audioStates}
                copiedStates={copiedStates}
                ttsSettings={ttsSettings}
                onDismissWarning={() => setShowLengthWarning(false)}
                onAudioToggle={handleAudioToggle}
                onCopy={handleCopy}
                getAudioIconType={getAudioIconType}
                onSavePrompt={(content) => { setSavingContent(content); setShowSaveDialog(true); }}
                onSaveToNotes={(content) => { setNoteContent(stripContextMarkers(content)); setShowSaveToNotes(true); }}
                messagesEndRef={messagesEndRef}
            />

            {/* Typing Indicator */}
            {typingUsers.length > 0 && (
                <div className="px-6 py-2 text-sm text-gray-500 dark:text-gray-400 italic">
                    {typingUsers.map(u => u.username).join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
                </div>
            )}

            {/* Processing Indicator */}
            {processingUser && (
                <div className="px-6 py-2 text-sm text-purple-600 dark:text-purple-400 italic flex items-center gap-2">
                    <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-purple-600 dark:bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-purple-600 dark:bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-purple-600 dark:bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                    <span>JPRO is processing {processingUser.username}'s request...</span>
                </div>
            )}

            {/* File preview area */}
            <FilePreviewArea
                uploadedFiles={uploadedFiles}
                onRemoveFile={removeFile}
            />

            {/* Input Area */}
            <ChatInputArea
                inputMessage={inputMessage}
                isLoading={isLoading}
                isRecording={isRecording}
                uploadedFiles={uploadedFiles}
                reasoningMode={reasoningMode}
                defaultReasoningMode={userDefaultReasoningMode}
                onPinReasoningMode={handlePinReasoningMode}
                chatSessionId={chatSessionId}
                recentMessages={messages
                    .slice(-6)
                    .filter(m => !m.isLoading && m.text && m.model !== 'system')
                    .map(m => ({ role: m.isUser ? 'user' : 'ai', content: m.text }))}
                onInputChange={handleInputChange}
                onSendMessage={handleSendMessage}
                onFileUpload={handleFileUpload}
                onStartRecording={startRecording}
                onStopRecording={stopRecording}
                onReasoningModeChange={setReasoningMode}
                onOpenSavedPrompts={() => setShowPromptsModal(true)}
            />

            {/* Drag and drop overlay for main chat area */}
            <DragDropOverlay isVisible={isDragging} size="large" />

            {/* Team Chat floating button */}
            {canAccessTeamChat() && !showTeamChat && !teamChatHidden && (
                <button
                    onClick={() => setShowTeamChat(true)}
                    title="Team Chat"
                    className="team-chat-pulse absolute bottom-[8.5rem] right-4 z-20 w-11 h-11 rounded-full shadow-lg hover:shadow-xl transition-colors duration-200 bg-blue-600/20 hover:bg-blue-500/30 text-blue-600 dark:text-blue-400 flex items-center justify-center group"
                >
                    <MessageCircle className="w-5 h-5" />
                    <span className="absolute right-full mr-3 px-2 py-1 rounded bg-gray-800 text-white text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        Team Chat
                    </span>
                </button>
            )}

            {/* Team Chat Drawer */}
            {canAccessTeamChat() && (
                <TeamChatDrawer isOpen={showTeamChat} onClose={() => setShowTeamChat(false)} />
            )}

            {/* Guide Modal */}
            <InfoModal
                isOpen={showGuide}
                onClose={() => setShowGuide(false)}
                title="AI Chat Assistant Guide"
            >
                <div className="space-y-4">
                    <section>
                        <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" />
                            What Can I Do?
                        </h4>
                        <p className="mb-2">JPRO can help with housing compliance questions, document analysis, video understanding, and much more.</p>
                    </section>

                    <section>
                        <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            File Upload & Analysis
                        </h4>
                        <p className="mb-2"><strong>Supported Files:</strong> PDF, Excel, Word, PowerPoint, Images, Audio, Video, CSV, Text (max 20MB each)</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                            <li><strong>Ask questions:</strong> "Summarize this document" or "What are the key points?"</li>
                            <li><strong>Compare documents:</strong> Upload multiple files and ask "What are the differences between these?"</li>
                            <li><strong>Extract information:</strong> "List all dates mentioned" or "Extract contact details"</li>
                            <li><strong>Analyze images:</strong> Upload photos and ask about their content</li>
                        </ul>
                    </section>

                    <section>
                        <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
                            <Video className="w-4 h-4" />
                            YouTube Video Understanding
                        </h4>
                        <p className="mb-2">Share YouTube URLs and chat about video content with full conversation memory.</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                            <li><strong>Summarize:</strong> Share a URL and ask "Summarize this video"</li>
                            <li><strong>Ask follow-ups:</strong> "What more does it talk about?" or "Explain the 3rd point"</li>
                            <li><strong>Compare videos:</strong> Share multiple URLs in the same chat to compare content</li>
                            <li><strong>Memory maintained:</strong> The assistant remembers all shared videos in the conversation</li>
                        </ul>
                    </section>

                    <section>
                        <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
                            <Link className="w-4 h-4" />
                            Web URL Analysis
                        </h4>
                        <p className="mb-2">Share any web URL and ask questions about its content.</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                            <li>Paste a URL in your message</li>
                            <li>Ask to summarize, analyze, or extract specific information</li>
                            <li>Follow-up questions maintain context</li>
                        </ul>
                    </section>

                    <section>
                        <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
                            <Mic className="w-4 h-4" />
                            Speech-to-Text
                        </h4>
                        <p className="mb-2">Use voice input to chat hands-free.</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                            <li>Click the microphone icon to start recording</li>
                            <li>Speak your question or message</li>
                            <li>Click again to stop - your speech converts to text automatically</li>
                            <li>Perfect for longer questions or when typing is inconvenient</li>
                        </ul>
                    </section>

                    <section>
                        <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
                            <Volume2 className="w-4 h-4" />
                            Text-to-Speech
                        </h4>
                        <p className="mb-2">Listen to AI responses with natural voice.</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                            <li>Click the speaker icon on any message to hear it</li>
                            <li>Use <strong>Voice Settings</strong> button to customize voice, style, and pace</li>
                            <li><strong>Voices:</strong> Choose from multiple natural-sounding voices</li>
                            <li><strong>Styles:</strong> Normal, professional, friendly, empathetic</li>
                            <li><strong>Pace:</strong> Slow, normal, or fast playback</li>
                        </ul>
                    </section>

                    <section>
                        <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
                            <Zap className="w-4 h-4 text-orange-500" />
                            Reasoning Modes
                        </h4>
                        <p className="mb-2">Choose between two AI reasoning modes for optimal performance.</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                            <li><strong>Fast Mode:</strong> Quick, factual responses (5-8 seconds) - ideal for straightforward questions, simple document analysis, and general chat</li>
                            <li><strong>Deep Mode:</strong> Complex reasoning (30-45 seconds) - best for detailed analysis, multi-document comparisons, and tasks requiring deep thinking</li>
                            <li><strong>Switch anytime:</strong> Select your preferred mode from the dropdown at the bottom right of the chat input</li>
                            <li><strong>Automatic optimization:</strong> Each mode uses specialized AI models and caching for the best speed and quality balance</li>
                        </ul>
                    </section>

                    <section>
                        <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
                            <Share2 className="w-4 h-4" />
                            Sharing Chats
                        </h4>
                        <p className="mb-2">Export and share your conversations easily.</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                            <li><strong>Export as PDF:</strong> Click the chat menu (⋮) → Share → Export as PDF to download a formatted PDF of the entire conversation</li>
                            <li><strong>Copy to Clipboard:</strong> Click the chat menu (⋮) → Share → Copy to Clipboard to copy the full chat as text</li>
                            <li><strong>Rename chats:</strong> Give your chats descriptive names using the Rename option for better organization</li>
                            <li><strong>Pin important chats:</strong> Pin frequently accessed chats to keep them at the top of your list</li>
                        </ul>
                    </section>

                    <section>
                        <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" />
                            Team Chat
                        </h4>
                        <p className="mb-2">Communicate with your team without leaving your conversation.</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                            <li>Click the blue chat bubble button in the bottom-right corner to open the team chat drawer</li>
                            <li>View your team conversations and send messages in real-time</li>
                            <li>Click the expand icon to open team chat as a full page</li>
                            <li>Close the drawer to return to your AI chat</li>
                        </ul>
                    </section>

                    <section>
                        <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
                            <Upload className="w-4 h-4" />
                            Tips for Best Results
                        </h4>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                            <li><strong>Be specific:</strong> Clear questions get better answers</li>
                            <li><strong>Use follow-ups:</strong> Build on previous responses for deeper insights</li>
                            <li><strong>Combine features:</strong> Upload a document AND share a video in one conversation</li>
                            <li><strong>Context matters:</strong> The assistant remembers your entire conversation</li>
                            <li><strong>Long conversations:</strong> You'll see a warning after 60 messages - start a new chat for best performance</li>
                        </ul>
                    </section>

                    <section>
                        <h4 className="font-semibold text-base mb-2">Example Questions</h4>
                        <div className="space-y-2">
                            <p className="italic bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                "Compare these two compliance documents and highlight the key differences"
                            </p>
                            <p className="italic bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                "Summarize this YouTube video and explain the main takeaways"
                            </p>
                            <p className="italic bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                "What are the LIHTC requirements mentioned in this document?"
                            </p>
                            <p className="italic bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                "Extract all the financial data from this Excel file and organize it"
                            </p>
                        </div>
                    </section>
                </div>
            </InfoModal>

            <SavedPromptsModal
                isOpen={showPromptsModal}
                onClose={() => setShowPromptsModal(false)}
                onUse={(content) => setInputMessage(content)}
                currentFeature="chat"
            />
            <SavePromptDialog
                isOpen={showSaveDialog}
                onClose={() => setShowSaveDialog(false)}
                content={savingContent}
                featureTag="chat"
            />
            {showSaveToNotes && (
                <AddToNotesModal
                    content={noteContent}
                    sourceType="ai_chat"
                    onClose={() => setShowSaveToNotes(false)}
                    theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
                />
            )}
        </div>
    );
}

export default UnifiedChat;